/**
 * Quiz UI style tokens.
 *
 * Two variants are maintained in parallel:
 *  - 'default': the pre-existing look shared by B2C, Company, and any app that
 *               does not mount <QuizStyleProvider>. String values MUST remain
 *               identical to their pre-refactor form — verify via git diff.
 *  - 'lc':      LC Learner Design System (apps/lc/DESIGN.md). Uses lc-* tokens
 *               defined in each consumer app's globals.css (LC natively; B2C/
 *               Company replicated in Task 0).
 */

import type { QuizStyleVariant } from './quiz-style-context'

// Semantic Tailwind palette names, retained for callers that import the object.
export const QUIZ_COLORS = {
  accent: 'blue-600',
  accentLight: 'blue-500',
  accentDark: 'blue-700',
  accentBg: 'blue-50',

  success: 'green-600',
  successLight: 'green-500',
  successDark: 'green-700',
  successBg: 'green-50',

  danger: 'red-600',
  dangerLight: 'red-500',
  dangerDark: 'red-700',
  dangerBg: 'red-50',

  surfaceBase: 'white',
  surfaceMuted: 'gray-50',
  surfaceSecondary: 'gray-100',

  textPrimary: 'gray-900',
  textSecondary: 'gray-700',
  textMuted: 'gray-500',
  textTertiary: 'gray-400',
} as const

export const QUIZ_OPTION_STATES = {
  default: 'border-gray-200 bg-white',
  hover: 'hover:border-blue-400 hover:bg-blue-50',
  selected: 'border-blue-500 bg-blue-100 text-blue-700 font-semibold',
  correct: 'border-green-500 bg-green-50 text-green-700',
  wrong: 'border-red-500 bg-red-50 text-red-700',
  disabled: 'border-gray-200 bg-gray-50 opacity-60',
} as const

export const QUIZ_INLINE_STYLES = {
  base: 'inline-flex items-center justify-center min-h-9 h-auto py-1 align-middle bg-white border border-gray-300 rounded-lg mx-1 px-3 text-lg font-semibold text-gray-900 transition-all text-center outline-hidden',
  input: 'w-auto px-2 cursor-text',
  inputPlaceholder: 'placeholder:text-gray-400 placeholder:font-normal',
  inputFocus: 'focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
  select: 'cursor-pointer',
  selectFocus: 'focus:border-blue-500 focus:ring-2 focus:ring-blue-100',
  box: 'cursor-pointer',
  boxEmpty: 'text-gray-400 font-normal',
  boxFilled: 'border-blue-500 bg-blue-50 text-blue-700',
  correct: 'border-green-500 bg-green-50 text-green-700',
  wrong: 'border-red-500 bg-red-50 text-red-700',
} as const

export const QUIZ_CHIP_STYLES = {
  base: 'min-w-[60px] px-4 py-1 bg-white border border-blue-500 text-blue-500 rounded-full font-medium transition-all shadow-2xs',
  hover: 'hover:bg-blue-500 hover:text-white hover:-translate-y-0.5',
  used: 'bg-gray-100 border-transparent text-gray-400 cursor-default shadow-none',
  disabled: 'cursor-default opacity-70',
} as const

// ----- Variant-aware style sets --------------------------------------------

type ButtonStyleSet = {
  primary: string
  primaryDisabled: string
  secondary: string
}

export const QUIZ_BUTTON_STYLES: Record<QuizStyleVariant, ButtonStyleSet> = {
  default: {
    primary:
      'py-4 px-8 rounded-xl bg-blue-600 text-white text-lg font-semibold transition-colors hover:bg-blue-700',
    primaryDisabled:
      'py-3 px-6 rounded-xl bg-gray-200 text-gray-400 text-lg font-bold cursor-not-allowed',
    secondary:
      'py-4 px-8 rounded-xl bg-gray-100 text-gray-400 text-lg font-semibold border-none hover:brightness-95',
  },
  lc: {
    primary:
      'h-12 min-w-[104px] rounded-xl bg-lc-key-500 px-5 py-3 text-[16px] ' +
      'font-semibold text-white transition-colors hover:bg-lc-key-600',
    primaryDisabled:
      'h-12 min-w-[104px] cursor-not-allowed rounded-xl bg-lc-v3-gray-400 ' +
      'px-5 py-3 text-[16px] font-semibold text-white',
    secondary:
      'h-10 rounded-[10px] bg-lc-v3-gray-100 px-4 py-2.5 text-[16px] ' +
      'font-medium text-lc-text-secondary',
  },
  aiq: {
    primary:
      'py-4 px-8 rounded-xl bg-lc-key-950 text-white text-lg font-semibold transition-colors hover:brightness-110',
    primaryDisabled:
      'py-3 px-6 rounded-xl bg-gray-200 text-gray-400 text-lg font-bold cursor-not-allowed',
    secondary:
      'py-4 px-8 rounded-xl bg-gray-100 text-gray-500 text-lg font-semibold border-none hover:brightness-95',
  },
}

type FeedbackStyleSet = {
  correct: { container: string; icon: string; title: string; text: string }
  wrong: { container: string; icon: string; title: string; text: string }
  // Used for descriptive/submitted (isCorrect === null) banner.
  submitted: { container: string; icon: string; title: string; text: string }
}

export const QUIZ_FEEDBACK_STYLES: Record<QuizStyleVariant, FeedbackStyleSet> = {
  default: {
    correct: {
      container: 'bg-linear-to-r from-green-50 to-emerald-50 border border-green-500',
      icon: 'bg-green-500',
      title: 'text-green-700',
      text: 'text-green-600',
    },
    wrong: {
      container: 'bg-linear-to-r from-red-50 to-rose-50 border border-red-500',
      icon: 'bg-red-500',
      title: 'text-red-700',
      text: 'text-red-600',
    },
    submitted: {
      container: 'bg-blue-50 border border-blue-200',
      icon: 'bg-blue-500',
      title: 'text-blue-700',
      text: 'text-blue-600',
    },
  },
  lc: {
    correct: {
      container: 'bg-lc-mint-100 border border-lc-mint-900 rounded-lc-lg shadow-lc-soft',
      icon: 'bg-lc-mint-900',
      title: 'text-lc-mint-dark',
      text: 'text-lc-gray-700',
    },
    wrong: {
      container: 'bg-lc-red-100 border border-lc-red-900 rounded-lc-lg shadow-lc-soft',
      icon: 'bg-lc-red-900',
      title: 'text-lc-red-dark',
      text: 'text-lc-gray-700',
    },
    submitted: {
      container: 'bg-lc-lightblue-100 border border-lc-lightblue-900 rounded-lc-lg shadow-lc-soft',
      icon: 'bg-lc-lightblue-900',
      title: 'text-lc-lightblue-dark',
      text: 'text-lc-gray-700',
    },
  },
  aiq: {
    correct: { container: 'bg-[#F2FAFA] border border-[#5AC6C8]', icon: 'bg-[#5AC6C8]', title: 'text-[#1E7E80]', text: 'text-[#1E7E80]' },
    wrong: { container: 'bg-red-50 border border-red-500', icon: 'bg-red-500', title: 'text-red-700', text: 'text-red-600' },
    submitted: { container: 'bg-[#E3F6F6] border border-[#5AC6C8]', icon: 'bg-[#5AC6C8]', title: 'text-[#1E7E80]', text: 'text-[#1E7E80]' },
  },
}

type ExplanationStyleSet = {
  toggleButton: string
  area: string
  areaCorrect: string
  areaWrong: string
  actionButton: string
}

export const QUIZ_EXPLANATION_STYLES: Record<QuizStyleVariant, ExplanationStyleSet> = {
  default: {
    toggleButton:
      'w-full flex justify-between items-center bg-blue-100 border-none text-gray-900 px-6 py-4 rounded-lg text-lg font-semibold cursor-pointer transition-all hover:brightness-95',
    area: 'w-full p-6 rounded-xl bg-white border border-gray-400 text-gray-600 leading-relaxed',
    areaCorrect: 'bg-green-50 border-green-500',
    areaWrong: 'bg-red-50 border-red-500',
    actionButton:
      'px-2 h-7 text-sm text-blue-600 rounded-md border border-blue-300 transition-colors hover:bg-blue-100',
  },
  lc: {
    toggleButton:
      'w-full flex justify-between items-center bg-lc-lightblue-100 border-none text-lc-gray-900 px-6 py-4 rounded-lc-lg text-lg font-semibold cursor-pointer transition-colors hover:bg-lc-lightblue-200 tracking-[-0.6px]',
    area: 'w-full p-6 rounded-lc-lg bg-white border border-lc-gray-200 text-lc-gray-700 leading-relaxed tracking-[-0.6px]',
    areaCorrect: 'bg-lc-mint-100 border-lc-mint-900',
    areaWrong: 'bg-lc-red-100 border-lc-red-900',
    actionButton:
      'px-4 h-8 text-sm font-medium text-white bg-lc-lightblue-900 rounded-lc-pill shadow-lc-3d-lightblue transition-all active:translate-y-0.5 active:shadow-lc-3d-lightblue-active tracking-[-0.6px]',
  },
  aiq: {
    toggleButton:
      'w-full flex justify-between items-center bg-[#E3F6F6] border-none text-gray-900 px-6 py-4 rounded-lg text-lg font-semibold cursor-pointer transition-all hover:brightness-95',
    area: 'w-full p-6 rounded-xl bg-white border border-gray-400 text-gray-600 leading-relaxed',
    areaCorrect: 'bg-[#F2FAFA] border-[#5AC6C8]',
    areaWrong: 'bg-red-50 border-red-500',
    actionButton: 'px-2 h-7 text-sm text-[#1E7E80] rounded-md border border-[#5AC6C8] transition-colors hover:bg-[#E3F6F6]',
  },
}

type OxStyleSet = {
  container: string
  button: string
  buttonHover: string
  buttonSelected: string
  buttonCorrect: string
  buttonWrong: string
  mark: string
  markSelected: string
  markCorrect: string
  markWrong: string
  label: string
  labelSelected: string
  labelCorrect: string
  labelWrong: string
}

export const QUIZ_OX_STYLES: Record<QuizStyleVariant, OxStyleSet> = {
  default: {
    container: 'flex gap-6 justify-center py-6',
    button:
      'flex flex-col items-center gap-4 p-6 border-2 border-gray-200 rounded-xl bg-white w-full max-w-[200px] transition-all duration-200',
    buttonHover: 'hover:border-blue-500 hover:-translate-y-1 hover:shadow-md',
    buttonSelected: 'border-blue-500 bg-blue-50',
    buttonCorrect: 'border-green-500 bg-green-50',
    buttonWrong: 'border-red-500 bg-red-50',
    mark: 'w-20 h-20 rounded-full flex items-center justify-center text-6xl font-extrabold bg-gray-100 text-gray-400 transition-all',
    markSelected: 'bg-blue-500 text-white',
    markCorrect: 'bg-green-500 text-white',
    markWrong: 'bg-red-500 text-white',
    label: 'text-2xl font-semibold text-gray-600',
    labelSelected: 'text-blue-700',
    labelCorrect: 'text-green-700',
    labelWrong: 'text-red-700',
  },
  lc: {
    container: 'flex gap-6 justify-center py-6',
    button:
      'flex flex-col items-center gap-4 p-6 border-2 border-lc-gray-200 rounded-lc-lg bg-white w-full max-w-[200px] transition-all duration-200',
    buttonHover: 'hover:border-lc-lightblue-900 hover:-translate-y-1 hover:shadow-lc-soft',
    buttonSelected: 'border-lc-lightblue-900 bg-lc-lightblue-200',
    buttonCorrect: 'border-lc-mint-900 bg-lc-mint-100',
    buttonWrong: 'border-lc-red-900 bg-lc-red-100',
    mark: 'w-20 h-20 rounded-full flex items-center justify-center text-6xl font-extrabold bg-lc-gray-100 text-lc-gray-500 transition-all',
    markSelected: 'bg-lc-lightblue-900 text-white',
    markCorrect: 'bg-lc-mint-900 text-white',
    markWrong: 'bg-lc-red-900 text-white',
    label: 'text-2xl font-semibold text-lc-gray-700 tracking-[-0.6px]',
    labelSelected: 'text-lc-lightblue-dark',
    labelCorrect: 'text-lc-mint-dark',
    labelWrong: 'text-lc-red-dark',
  },
  aiq: {
    container: 'flex gap-6 justify-center py-6',
    button: 'flex flex-col items-center gap-4 p-6 border-2 border-gray-200 rounded-xl bg-white w-full max-w-[200px] transition-all duration-200',
    buttonHover: 'hover:border-[#5AC6C8] hover:-translate-y-1 hover:shadow-md',
    buttonSelected: 'border-[#5AC6C8] bg-[#F2FAFA]',
    buttonCorrect: 'border-[#5AC6C8] bg-[#F2FAFA]',
    buttonWrong: 'border-red-500 bg-red-50',
    mark: 'w-20 h-20 rounded-full flex items-center justify-center text-6xl font-extrabold bg-gray-100 text-gray-400 transition-all',
    markSelected: 'bg-[#5AC6C8] text-white',
    markCorrect: 'bg-[#5AC6C8] text-white',
    markWrong: 'bg-red-500 text-white',
    label: 'text-2xl font-semibold text-gray-600',
    labelSelected: 'text-[#1E7E80]',
    labelCorrect: 'text-[#1E7E80]',
    labelWrong: 'text-red-700',
  },
}

export const QUIZ_NAV_STYLES = {
  item: 'w-10 h-10 rounded-full flex items-center justify-center font-bold text-base transition-all border-none bg-gray-200 text-gray-900',
  active: 'bg-gray-900 text-white scale-110 shadow-2xs',
  correct: 'bg-green-500 text-white',
  wrong: 'bg-red-500 text-white',
} as const

// assessmentMode 하단 고정 '다음' 버튼 (full-width, rounded-none). 색만 variant별로.
type SessionNextStyleSet = { enabled: string; disabled: string }
export const QUIZ_SESSION_NEXT_STYLES: Record<QuizStyleVariant, SessionNextStyleSet> = {
  default: {
    enabled: 'w-full py-4 text-lg font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors',
    disabled: 'w-full py-4 text-lg font-bold text-white bg-gray-300 cursor-not-allowed',
  },
  lc: {
    enabled: 'w-full py-4 text-lg font-bold text-white bg-lc-orange-900 hover:brightness-105 transition-all',
    disabled: 'w-full py-4 text-lg font-bold text-white bg-lc-gray-400 cursor-not-allowed',
  },
  aiq: {
    enabled: 'h-14 w-full bg-lc-key-950 text-lc-v3-body-1 font-bold text-white transition-colors hover:bg-lc-key-900',
    disabled: 'h-14 w-full cursor-not-allowed bg-lc-v3-gray-300 text-lc-v3-body-1 font-bold text-white',
  },
}

// 문제 카드 우측 하단에 놓는 assessment 전용 액션. 화면을 덮지 않도록 compact button을 쓴다.
export const QUIZ_SESSION_CARD_END_STYLES: Record<QuizStyleVariant, SessionNextStyleSet> = {
  default: {
    enabled: 'min-w-36 rounded-xl px-8 py-3 text-lg font-bold text-white bg-indigo-600 hover:bg-indigo-700 transition-colors',
    disabled: 'min-w-36 rounded-xl px-8 py-3 text-lg font-bold text-white bg-gray-300 cursor-not-allowed',
  },
  lc: {
    enabled:
      'h-12 min-w-[104px] rounded-xl bg-lc-key-900 px-5 py-3 text-lc-v3-body-1 ' +
      'font-semibold text-white transition-colors hover:bg-lc-key-950 ' +
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500',
    disabled:
      'h-12 min-w-[104px] cursor-not-allowed rounded-xl bg-lc-v3-gray-400 px-5 py-3 ' +
      'text-lc-v3-body-1 font-semibold text-white',
  },
  aiq: {
    enabled: 'min-w-36 rounded-xl px-8 py-3 text-lg font-bold text-white bg-[#222B4A] hover:brightness-110 transition-all tracking-[1px]',
    disabled: 'min-w-36 rounded-xl px-8 py-3 text-lg font-bold text-white bg-gray-300 cursor-not-allowed tracking-[1px]',
  },
}

// student 렌더러의 인터랙티브(풀이 전) 색 표면. default/lc는 현행 lc-* 값을 그대로 유지(회귀 0).
type InteractiveStyleSet = {
  blankEmpty: string
  blankActive: string
  blankFilled: string
  optionIdle: string
  optionIdleHover: string
  optionSelected: string
  badgeIdle: string
  badgeIdleHover: string
  badgeSelected: string
}
const LC_INTERACTIVE: InteractiveStyleSet = {
  blankEmpty: 'border-2 border-lc-gray-400 [box-shadow:0px_4px_0px_rgba(0,0,0,0.1)] hover:text-lc-lightblue-900 hover:border-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_rgba(85,195,255,0.3)]',
  blankActive: 'border-2 border-lc-lightblue-900 bg-lc-lightblue-200 min-w-[96px]',
  blankFilled: 'bg-lc-lightblue-200',
  optionIdle: 'text-gray-900 border-lc-gray-400 [box-shadow:0_4px_0_rgba(0,0,0,0.1)]',
  optionIdleHover: 'hover:text-lc-lightblue-900 hover:border-lc-lightblue-900 hover:[box-shadow:0_4px_0_rgba(85,195,255,0.3)]',
  optionSelected: 'text-lc-lightblue-dark bg-lc-lightblue-200 border-lc-lightblue-900 [box-shadow:0_4px_0_rgba(0,0,0,0.1)]',
  badgeIdle: 'text-lc-gray-500 bg-lc-gray-100',
  badgeIdleHover: 'group-hover:text-lc-lightblue-900 group-hover:bg-lc-lightblue-200',
  badgeSelected: 'text-white bg-lc-lightblue-900',
}
const LC_V3_INTERACTIVE: InteractiveStyleSet = {
  blankEmpty: 'border-lc-v3-gray-400 bg-white hover:border-lc-key-500',
  blankActive: 'border-lc-key-500 bg-[#ddf3ff]',
  blankFilled: 'border-lc-key-500 bg-[#ddf3ff]',
  optionIdle: 'border-lc-v3-gray-400 bg-white text-lc-text-primary',
  optionIdleHover: 'hover:border-lc-key-500 hover:bg-lc-key-50',
  optionSelected: 'border-lc-key-500 bg-[#ddf3ff] text-lc-text-primary',
  badgeIdle: 'bg-lc-v3-gray-100 text-lc-text-tertiary',
  badgeIdleHover: 'group-hover:bg-lc-key-50 group-hover:text-lc-key-500',
  badgeSelected: 'bg-lc-key-500 text-white',
}
export const QUIZ_INTERACTIVE_STYLES: Record<QuizStyleVariant, InteractiveStyleSet> = {
  default: LC_INTERACTIVE,
  lc: LC_V3_INTERACTIVE,
  aiq: {
    blankEmpty: 'border-lc-key-500 bg-lc-bg-key-light text-lc-key-500 hover:border-lc-key-600',
    blankActive: 'border-lc-key-500 bg-lc-bg-key-light text-lc-key-500',
    blankFilled: 'border-lc-key-500 bg-lc-bg-key-light text-lc-key-500',
    optionIdle: 'border-lc-v3-gray-400 bg-white text-lc-text-primary',
    optionIdleHover: 'hover:border-lc-key-500 hover:bg-lc-key-50',
    optionSelected: 'border-lc-key-500 bg-lc-bg-key-light text-lc-text-primary',
    badgeIdle: 'bg-lc-v3-gray-100 text-lc-text-tertiary',
    badgeIdleHover: 'group-hover:bg-lc-key-50 group-hover:text-lc-key-500',
    badgeSelected: 'bg-lc-key-500 text-white',
  },
}
